import { LoadingButton } from '@mui/lab';
import { Box,Button, Card, Grid, Stack, Typography } from '@mui/material';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { FormProvider, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import LinearProgressBar from 'src/components/progress-bar/progressBar.component';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import TimeZoneSelect from 'src/components/time-zone/time-zone.component';
import { useTranslation } from 'react-i18next';

const getAIKeySaveState = (value, isChanged) => {
  const trimmedValue = String(value || '').trim();
  if (!isChanged || trimmedValue.includes('*')) {
    return { shouldUpdate: false, value: '' };
  }
  return { shouldUpdate: true, value: trimmedValue };
};

const BrandInfoComponent = () => {
  const { t: translate } = useTranslation();
  const isOrgUser = CommonHelper.GetBrandInfoFromLocalStorage('is_org_user');
  const isReseller = CommonHelper.GetBrandInfoFromLocalStorage("is_reseller");
  const activeBrandId = CommonHelper.GetCurrentBrandId();
  const isUserHasPermission = CommonHelper.CheckForPermissionAvailableForUser();
  const maxFileSizeInBytes = 10485760;

  const { showToast } = useToaster();

  const [brandId, setBrandId] = useState(null);
  const [brandName, setBrandName] = useState('');
  const [imageUrl, setBrandImage] = useState('');
  const [brandDescription, setBrandDescription] = useState('');
  const [formSchema, setFormSchema] = useState({});
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const isOrgAdminHasPermission = CommonHelper.CheckForPermissionAvailableForUser(CommonHelper.PermissionList.ORG_ADMIN);
  const [isSaveChangesLoading, setSaveChangesLoader] = useState(false);
  const [isLeaveBrandLoading, setLeaveBrandLoader] = useState(false);
  const [fileUploadprogress, setFileUploadprogress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [timeZone, setTimeZone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [openAIAPIKey, setOpenAIAPIKey] = useState('');
  const [hasOpenAIAPIKey, setHasOpenAIAPIKey] = useState(false);
  const [geminiAPIKey, setGeminiAPIKey] = useState('');
  const [hasGeminiAPIKey, setHasGeminiAPIKey] = useState(false);
  useEffect(() => {
    generateFormModelSchema();
    getBrandInfo(activeBrandId);
  }, []);

  const getBrandInfo = () => {
    let getProps = {
      url: userModuleURL.fetchBrandInfo,
      successCallback: brandSuccessCallBack,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };

  const brandSuccessCallBack = (data, message) => {
    setBrandId(data.id);
    setBrandName(data.brand_name);
    setTimeZone(data.timezone);
    setBrandDescription(data.description);
    setBrandImage(data.brand_image);
    setSelectedLanguage(data.language ? data.language : 'english');
    if (data.has_openai_api_key) setOpenAIAPIKey('**************');
    setHasOpenAIAPIKey(false);
    if (data.has_gemini_api_key) setGeminiAPIKey('**************');
    setHasGeminiAPIKey(false);
  };

  const failureCallback = (message) => {
    showToast(message, 'e');
    setSaveChangesLoader(false);
    setLeaveBrandLoader(false);
    setConfirmDeleteOpen(false)
  };

  const handleSubmitForm = () => {
    let userForm = {
      brandName: brandName,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, userForm);
    if (isValidForm) {
      setFormValidationErrors({});
      onSubmit();
    } else setFormValidationErrors(formValidationErrors);
  };

  const onSubmit = () => {
    setSaveChangesLoader(true);
    const formattedTimeZone = timeZone.split(':')[0].trim();
    const openAIKeySave = getAIKeySaveState(openAIAPIKey, hasOpenAIAPIKey);
    const geminiKeySave = getAIKeySaveState(geminiAPIKey, hasGeminiAPIKey);
    let brandProps = {
      brand_name: brandName,
      brand_image: imageUrl,
      description: brandDescription,
      timezone: formattedTimeZone,
      language: selectedLanguage,
      openai_api_key: openAIKeySave.value,
      has_openai_api_key: openAIKeySave.shouldUpdate,
      gemini_api_key: geminiKeySave.value,
      has_gemini_api_key: geminiKeySave.shouldUpdate,
    };
    let postProps = {
      url: userModuleURL.updateBrand,
      body: brandProps,
      successCallback: brandDetailsCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const brandDetailsCallback = (data, message) => {
    CommonHelper.SetAllBrandDetials();
    setSaveChangesLoader(false);
    showToast(message, 's');
    window.location.reload();
  };

const confirmDelete = () => {
    setConfirmDeleteOpen(false);
    let brandprops = { brand_id: brandId };
    let postProps = {
      url: userModuleURL.deleteBrand,
      body: brandprops,
      successCallback: updateSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const updateSuccessCallback = (data, message) => {
      window.location.reload();
  };

  const handleDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    handleFileUpload(file);
  };

  const fileUploadSuccessCallback = (data, message) => {
    setBrandImage(data.media_data[0].media_url);
  };

  const handleFileUpload = (file) => {
    let isFileInCorrectFormat = CommonHelper.SupportedImageFormat.some((a) => a.includes(file.type));
    if (isFileInCorrectFormat) {
      if (file.size < maxFileSizeInBytes) {
        setFileUploadprogress(1);
        let formData = new FormData();
        formData.append('fileupload', file);
        axios
          .post(imageModeuleUrl.uploadMedia, formData, {
            headers: HttpServices.GetRequestHeader('form', true),
            onUploadProgress: (data) => {
              let loadedProgress = Math.round((100 * data.loaded) / data.total);
              setFileUploadprogress(loadedProgress > 90 ? 90 : loadedProgress);
            },
          })
          .then((result) => result)
          .then((response) => {
            setFileUploadprogress(100);
            if (response.status === 200 && response.data.http_code === 200)
              fileUploadSuccessCallback(response.data.data, translate('common.file_uploaded_successfully'));
            else showToast(translate('common.something_went_wrong'), 'e');
            setFileUploadprogress(0);
          })
          .catch((error) => {
            setFileUploadprogress(0);
            showToast(error.toString(), 'e');
          });
      } else {
        showToast(`${translate('brand.max_image_size')} 10MB. ${translate('brand.please_select_below')}`, 'e');
      }
    } else {
      showToast(
        `${translate('common.we_only_support')} JPEG, ${translate('common.and')} PNG ${translate(
          'common.file_formats'
        )}. ${translate('common.selected_file_not_in_format')}`,
        'e'
      );
    }
  };

  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'brandName':
        setBrandName(value);
        break;
      case 'brandDescription':
        setBrandDescription(value);
        break;
      case 'timezone':
        setTimeZone(value);
        break;
      case 'language':
        setSelectedLanguage(value);
        break;
      case 'openAIAPIKey':
        setHasOpenAIAPIKey(true);
        setOpenAIAPIKey(value?.includes('*') ? '' : value);
        break;
      case 'geminiAPIKey':
        setHasGeminiAPIKey(true);
        setGeminiAPIKey(value?.includes('*') ? '' : value);
        break;
      default:
        break;
    }
  };

  const leaveCallback = (data, message) => {
    window.location.href = window.location.origin;
  };

  const handleLeave = () => {
    setConfirmOpen(true);
  };
  const handleDelete = () => {
    setConfirmDeleteOpen(true);
  };

  const handleLeaveBrand = () => {
    setLeaveBrandLoader(true);
    let postProps = {
      url: userModuleURL.leaveBrand,
      successCallback: leaveCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };

  const generateFormModelSchema = () => {
    let fields = [ValidationTool.GenerateSchema('brandName', translate('brand.brand_name'), 'TEXT', true)];
    const schema = ValidationTool.GenerateSchemaModel(fields, translate);
    setFormSchema(schema);
  };

  const handleDeleteBrandImage = () => {
    setBrandImage('');
  };

  const defaultValues = {
    brandName: brandName || '',
    imageUrl: imageUrl || '',
    brandDescription: brandDescription || '',
    timeZone: timeZone || '',
  };

  const methods = useForm({
    defaultValues,
  });

  const { handleSubmit } = methods;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box display="flex" flexDirection="column" height="100%">
            <Card sx={{ pt: 10, pb: 5, px: 3, textAlign: 'center', flexGrow: 1 }}>
              <Box position="relative" display="inline-block">
                <RHFUploadAvatar
                  name="imageUrl"
                  accept="image/*"
                  onDrop={handleDrop}
                  handleDeleteProfiles={handleDeleteBrandImage}
                  url={imageUrl}
                  helperText={
                    <Typography
                      variant="caption"
                      sx={{
                        mt: 2,
                        mx: 'auto',
                        display: 'block',
                        textAlign: 'center',
                        color: 'text.secondary',
                      }}
                    >
                      {translate('brand.allowed')} *.jpeg, *.jpg, *.png, *.gif
                      <br /> {translate('brand.max_size_of')} 5Mb
                    </Typography>
                  }
                />
              </Box>
              <Stack alignItems="center" justifyContent="center">
                {!isOrgUser && !isReseller && (
                  <LoadingButton variant="contained" loading={isLeaveBrandLoading} onClick={handleLeave}>
                    {translate('profile.profile_details.leave_brand_button')}
                  </LoadingButton>
                )}
                {isOrgUser && !isReseller && isOrgAdminHasPermission && (
                  <Button variant="contained" color="error" onClick={handleDelete}>
                    {translate('common.delete')}
                  </Button>
                )}
                <ConfirmationDialog
                  open={confirmOpen}
                  onClose={() => setConfirmOpen(false)}
                  onConfirm={handleLeaveBrand}
                  title={translate('profile.profile_details.confirm_dialog.title')}
                  message={translate('profile.profile_details.confirm_dialog.message')}
                />
              </Stack>
            </Card>
            {fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <Box display="flex" flexDirection="column">
            <Card sx={{ p: 3, flexGrow: 1 }}>
              <Box
                sx={{
                  display: 'grid',
                  rowGap: 3,
                  columnGap: 2,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                }}
              >
                <RHFTextField
                  name="brandName"
                  label={translate('brand.brand_name')}
                  value={brandName}
                  onChange={handleInputChange}
                  error={formValidationErrors?.brandName}
                  helperText={formValidationErrors?.brandName}
                />
              </Box>
              <Box sx={{ mt: 3, p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  AI Chat Keys
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    rowGap: 3,
                    columnGap: 2,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField
                    name="openAIAPIKey"
                    label={hasOpenAIAPIKey || openAIAPIKey?.includes('*') ? 'OpenAI API Key configured' : 'OpenAI API Key'}
                    value={openAIAPIKey}
                    onChange={handleInputChange}
                  />
                  <RHFTextField
                    name="geminiAPIKey"
                    label={hasGeminiAPIKey || geminiAPIKey?.includes('*') ? 'Gemini API Key configured' : 'Gemini API Key'}
                    value={geminiAPIKey}
                    onChange={handleInputChange}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  rowGap: 3,
                  columnGap: 2,
                  gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  mt: 3,
                }}
              >
                <Stack>
                  <TimeZoneSelect name="timezone" value={timeZone} onChange={handleInputChange} />
                </Stack>
                {/* <LanguageSelect selectedLanguage={selectedLanguage} handleChange={handleInputChange} /> */}
              </Box>
              <Stack spacing={3} alignItems="flex-end" sx={{ mt: 3 }}>
                <RHFTextField
                  name="brandDescription"
                  multiline
                  rows={5}
                  label={translate('manage_brand.brand_info.brand_description')}
                  value={brandDescription}
                  onChange={handleInputChange}
                />
              </Stack>
              {isUserHasPermission && (
                <Stack alignItems="flex-end">
                  <Stack spacing={3} direction="row" sx={{ mt: 3 }}>
                    <LoadingButton type="submit" variant="contained" loading={isSaveChangesLoading}>
                      {translate('common.save_changes')}
                    </LoadingButton>
                  </Stack>
                </Stack>
              )}
            </Card>
          </Box>
        </Grid>
      </Grid>
        <ConfirmationDialog
              open={confirmDeleteOpen}
              onClose={() => setConfirmDeleteOpen(false)}
              onConfirm={confirmDelete}
              title={translate('common.confirm_deletion')}
              message={`${translate('common.confirm_delete_brand')}?`}
            />
    </FormProvider>
  );
};

export default BrandInfoComponent;
